import React, {useState} from 'react';
import {HeaderButton, HeaderButtonProps} from './HeaderButton';
import {DataEnrichment, Information} from '@carbon/icons-react';
import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {useCustomer} from '../../../hooks/api/metadata';
import Select from 'react-select';
import {EnrichmentMode} from '../../../shared/fields/enrichment';
import {useAccountQueryParams} from '../gridStore';
import {useScoringRun} from '../../../context/ScoringRunContext';
import {pluralize} from '../../../lib/helpers';
import {AccountSelection} from '../../../shared/api/api';
import {useEnrichAccounts} from '../../../hooks/useEnrichAccounts';
import {getPrimaryAgentField} from '../../../shared/agent';
import {ensure} from '../../../shared/util';
import {getFieldLabel, FieldDefinition} from '../../../shared/fields/types';

export const EnrichButton = ({isDisabled, ...props}: HeaderButtonProps) => {
  const {isOpen, onClose, onOpen} = useDisclosure();
  const scoringRun = useScoringRun();
  const scoringRunId = scoringRun?._id;
  const queryParams = useAccountQueryParams();

  return (
    <>
      <HeaderButton
        onClick={onOpen}
        leftIcon={<DataEnrichment />}
        {...props}
        isDisabled={!scoringRun || isDisabled}
      >
        Enrich
      </HeaderButton>
      <EnrichAccountsModal
        modalTitle="Enrich Accounts in View"
        accountSelection={{
          name: 'query',
          query: {
            ...queryParams,
            runId: scoringRunId,
          },
        }}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
};

export const EnrichAccountsModal = ({
  accountSelection,
  isOpen,
  onClose,
  modalTitle,
}: {
  accountSelection: AccountSelection;
  isOpen: boolean;
  onClose: () => void;
  modalTitle: string;
}) => {
  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalCloseButton />
        <EnrichAccountsModalContent
          accountSelection={accountSelection}
          onClose={onClose}
        />
      </ModalContent>
    </Modal>
  );
};

const EnrichAccountsModalContent = ({
  accountSelection,
  onClose,
}: {
  accountSelection: AccountSelection;
  onClose: () => void;
}) => {
  const customer = useCustomer();

  const agentFields = (customer.agents ?? [])
    .filter(({status}) => status === 'published')
    .map((agent) => {
      const field = ensure(
        getPrimaryAgentField({
          fieldDefinitions: customer.fieldDefinitions,
          agentId: agent.id,
        })
      );
      return {
        value: field.id,
        label: agent.label,
        field,
      };
    });

  const roleCountFields = (customer.fieldDefinitions ?? [])
    .filter(({type}) => type === 'roleCount')
    .filter(({status}) => status === 'published')
    .map((field) => ({
      value: field.id,
      label: getFieldLabel(field),
      field,
    }));
  const options = [...agentFields, ...roleCountFields].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const [selectedField, setSelectedField] = useState<FieldDefinition | null>(
    null
  );
  const [enrichmentOption, setEnrichmentOption] = useState<
    'new' | 'all' | 'stale'
  >('new');
  const [cutoffDate, setCutoffDate] = useState<string>('');
  const fieldDefinitionId = selectedField?.id;

  const enrichmentMode: EnrichmentMode =
    enrichmentOption === 'all'
      ? {type: 'all'}
      : enrichmentOption === 'stale' && cutoffDate
        ? {
            type: 'stale',
            cutoffDate: new Date(cutoffDate),
            includeNewAccounts: true,
          }
        : {type: 'new'};

  const {
    creditsNeeded,
    insufficientCredits,
    runEnrichment,
    numAccountsToEnrich,
    isPending,
  } = useEnrichAccounts({
    fieldDefinitionId,
    enrichmentMode,
    accountSelection,
  });

  const toast = useToast();
  const onEnrich = () => {
    runEnrichment.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: 'Enrichment task started',
          description: 'We will send an email when it has finished.',
          status: 'success',
        });
        onClose();
      },
    });
  };

  return (
    <>
      <ModalBody as={Flex} flexDir="column" gap="8" mb="4">
        <FormControl>
          <FormLabel mb={3}>Select an Agent</FormLabel>
          <Select
            isMulti={false}
            onChange={(selected) => {
              if (!selected) {
                return;
              }

              setSelectedField(selected.field);
            }}
            options={options}
            placeholder="Select a field"
            value={options.find(
              (option) =>
                option.value.toString() === selectedField?.id.toString()
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel mb={3}>Select Accounts</FormLabel>
          <RadioGroup
            value={enrichmentOption}
            onChange={(value) =>
              setEnrichmentOption(value as 'new' | 'all' | 'stale')
            }
          >
            <Stack spacing={3}>
              <Radio value="all">All selected accounts</Radio>
              <Radio value="new">Accounts with no data</Radio>
              <Radio value="stale">
                Accounts with stale data, last enriched before:
              </Radio>
              {enrichmentOption === 'stale' && (
                <HStack>
                  <Input
                    type="date"
                    value={cutoffDate}
                    onChange={(e) => setCutoffDate(e.target.value)}
                    ml={6}
                    maxW="200px"
                    placeholder="Select date"
                  />
                  <Tooltip
                    label="Accounts with data older than this date will be enriched."
                    placement="right"
                    hasArrow
                  >
                    <Information size={16} />
                  </Tooltip>
                </HStack>
              )}
            </Stack>
          </RadioGroup>
        </FormControl>
      </ModalBody>
      <Divider />
      <ModalFooter as={Flex} flexDir="column" gap="2" h="auto" py="4">
        <Flex justifyContent="space-between" w="100%">
          <Text>Required credits:</Text>
          <Text>{creditsNeeded?.toLocaleString() ?? '-'}</Text>
        </Flex>
        <Flex justifyContent="space-between" w="100%">
          <Text>Remaining credits:</Text>
          <Text>{customer.credits.toLocaleString()}</Text>
        </Flex>

        <Tooltip label="Insufficient credits" isDisabled={!insufficientCredits}>
          <Button
            colorScheme="kbuttonblue"
            fontWeight="normal"
            size="md"
            width="100%"
            isLoading={isPending}
            isDisabled={
              !numAccountsToEnrich ||
              insufficientCredits ||
              (enrichmentOption === 'stale' && !cutoffDate)
            }
            onClick={onEnrich}
            alignSelf="flex-end"
            mt="2"
          >
            {numAccountsToEnrich !== undefined
              ? `Enrich ${pluralize(numAccountsToEnrich, 'account', 'accounts')}`
              : 'Enrich Accounts'}
          </Button>
        </Tooltip>
      </ModalFooter>
    </>
  );
};
